import React, { memo, useMemo } from 'react';
import { IconAddPhotoOutline, IconNoteAddOutline } from '../../icons';
import cn from '../../utils/cn';
import { DropZoneVariant, useDropZone } from './DropZone';
import Alert, { AlertSize, AlertVariant } from '../../atoms/alert/Alert';
import ErrorStateCard from '../../atoms/cards/error-state-card/ErrorStateCard';
import { ErrorCode } from 'react-dropzone';

const Root = memo(
  ({ className, children, ...props }: React.ComponentPropsWithoutRef<'div'>) => {
    const { dragEnter, getRootProps, getInputProps, variant } = useDropZone();

    return (
      <div
        className={cn(
          'flex cursor-pointer flex-col items-center justify-center gap-4 rounded-lg bg-neutral-100 p-4 transition-colors',
          'outline-dashed outline-2 outline-offset-[-2px] outline-neutral-300',
          {
            'bg-white': variant === DropZoneVariant.MEDIA,
          },
          {
            'bg-neutral-200': dragEnter,
          },
          className,
        )}
        {...getRootProps()}
        {...props}
      >
        <input {...getInputProps()} />
        {children}
      </div>
    );
  },
);

Root.displayName = 'DropZone.DropArea.Root';

type IconProps = {
  icon?: React.ComponentType<React.ComponentPropsWithoutRef<'svg'>>;
} & React.ComponentPropsWithoutRef<'svg'>;

const Icon = memo(({ icon, className, ...props }: IconProps) => {
  const { variant } = useDropZone();

  const Icon = useMemo(() => {
    if (icon) {
      return icon;
    }

    return variant === DropZoneVariant.DOCUMENT
      ? IconNoteAddOutline
      : IconAddPhotoOutline;
  }, [icon, variant]);

  return (
    <Icon
      className={cn(
        'text-primary size-6 shrink-0',
        { 'size-8': variant === DropZoneVariant.MEDIA },
        className,
      )}
      {...props}
    />
  );
});

Icon.displayName = 'DropZone.DropArea.Icon';

const Title = memo(
  ({ className, ...props }: Omit<React.ComponentPropsWithoutRef<'div'>, 'children'>) => {
    const { multiple, variant } = useDropZone();

    return (
      <div
        className={cn(
          'shrink-0 text-center text-base font-semibold text-neutral-700',
          className,
        )}
        {...props}
      >
        {variant === DropZoneVariant.DOCUMENT ? (
          <>{multiple ? 'Ajouter vos documents ici !' : 'Ajouter votre document ici !'}</>
        ) : (
          <>{multiple ? 'Ajouter vos médias ici !' : 'Ajouter votre média ici !'}</>
        )}
      </div>
    );
  },
);

Title.displayName = 'DropZone.DropArea.Title';

const Description = memo(
  ({ className, ...props }: Omit<React.ComponentPropsWithoutRef<'div'>, 'children'>) => {
    const { multiple, variant } = useDropZone();

    return (
      <div className={cn('text-center text-sm text-neutral-600', className)} {...props}>
        {variant === DropZoneVariant.DOCUMENT ? (
          <>
            {multiple
              ? 'Cliquez ou déposez vos documents pdf, docx, xlsx, pptx, txt, jpg ou png ici.'
              : 'Cliquez ou déposez votre document pdf, docx, xlsx, pptx, txt, jpg ou png ici.'}
          </>
        ) : (
          <>
            {multiple
              ? 'Cliquez ou déposez vos médias JPG, PNG, GIF ou WebP ici.'
              : 'Cliquez ou déposez votre média JPG, PNG, GIF ou WebP ici.'}
          </>
        )}
      </div>
    );
  },
);

Description.displayName = 'DropZone.DropArea.Description';

type ErrorProps = {
  onRetry?: () => void;
} & React.ComponentPropsWithoutRef<'div'>;

const Error = memo(({ className, onRetry, ...props }: ErrorProps) => {
  const { maxSizeMB, maxFiles, errorCode, clearError } = useDropZone();

  const maxSizeMBString = useMemo(() => maxSizeMB.toString(), [maxSizeMB]);

  if (!errorCode) {
    return null;
  }

  if (errorCode === ErrorCode.FileTooLarge) {
    return (
      <Alert
        variant={AlertVariant.WARNING}
        size={AlertSize.SMALL}
        className={cn('w-full', className)}
        {...props}
      >
        La taille de votre fichier dépasse {maxSizeMBString} Mo.
      </Alert>
    );
  }

  if (errorCode === ErrorCode.FileInvalidType) {
    return (
      <Alert
        variant={AlertVariant.WARNING}
        size={AlertSize.SMALL}
        className={cn('w-full', className)}
        {...props}
      >
        Type de fichier invalide.
      </Alert>
    );
  }

  if (errorCode === ErrorCode.TooManyFiles) {
    return (
      <Alert
        variant={AlertVariant.WARNING}
        size={AlertSize.SMALL}
        className={cn('w-full', className)}
        {...props}
      >
        Vous avez atteint la limite de {maxFiles} fichier.
      </Alert>
    );
  }

  return (
    <ErrorStateCard onClick={onRetry ?? clearError} className={className} {...props} />
  );
});

Error.displayName = 'DropZone.DropArea.Error';

const FileLayout = memo(({ errorCode }: { errorCode?: ErrorCode }) => (
  <div className="flex items-center gap-4">
    <Icon />

    <div className="flex items-center gap-4">
      <Title />
      {errorCode ? <Error className="w-fit" /> : <Description />}
    </div>
  </div>
));

FileLayout.displayName = 'DropZone.DropArea.FileLayout';

const ImageLayout = memo(() => (
  <div className="flex flex-col items-center gap-4">
    <Icon />

    <div className="flex flex-col items-center gap-1.5">
      <Title />
      <Description />
    </div>

    <Error />
  </div>
));

ImageLayout.displayName = 'DropZone.DropArea.ImageLayout';

const DropArea = memo(({ ...props }: React.ComponentPropsWithoutRef<typeof Root>) => {
  const { variant, errorCode } = useDropZone();

  return (
    <Root {...props}>
      {variant === DropZoneVariant.MEDIA ? (
        <ImageLayout />
      ) : (
        <FileLayout errorCode={errorCode} />
      )}
    </Root>
  );
});

DropArea.displayName = 'DropZone.DropArea';

const DropAreaCompound = Object.assign(DropArea, {
  Root,
  Icon,
  Title,
  Description,
  Error,
});

export default DropAreaCompound;
